import { Check, ChevronRight, LockKeyhole, Play, Sparkles, TerminalSquare } from "lucide-react";
import type { World } from "../types";

type WorldMapProps = {
  worlds: World[];
  completed: string[];
  onOpenWorld: (world: World) => void;
  onOpenCoding: () => void;
};

function completedInWorld(world: World, completed: string[]) {
  return world.quests.filter((quest) => completed.includes(quest.id)).length;
}

function unlockedAt(index: number, worlds: World[], completed: string[]) {
  if (index === 0) return true;
  const previous = worlds[index - 1];
  return completedInWorld(previous, completed) >= Math.ceil(previous.quests.length * 0.6);
}

export function WorldMap({ worlds, completed, onOpenWorld, onOpenCoding }: WorldMapProps) {
  const nextWorld = worlds.find((world, index) => unlockedAt(index, worlds, completed) && completedInWorld(world, completed) < world.quests.length) ?? worlds[0];
  const nextQuest = nextWorld.quests.find((quest) => !completed.includes(quest.id)) ?? nextWorld.quests[0];
  const nextDone = completedInWorld(nextWorld, completed);
  const nextPercent = Math.round((nextDone / nextWorld.quests.length) * 100);

  return (
    <main className="map-screen">
      <img className="world-art" src="/assets/world-map.png" alt="六座漂浮的 AI 学习岛屿" />
      <div className="map-vignette" aria-hidden="true" />
      <section className="world-nodes" aria-label="六个学习世界">
        {worlds.map((world, index) => {
          const done = completedInWorld(world, completed);
          const unlocked = unlockedAt(index, worlds, completed);
          const complete = done === world.quests.length;
          return (
            <button
              key={world.id}
              type="button"
              className={`world-node ${unlocked ? "unlocked" : "locked"} ${complete ? "complete" : ""}`}
              style={{
                left: `${world.position.x}%`,
                top: `${world.position.y}%`,
                "--world-color": world.color,
                "--world-accent": world.accent,
              } as React.CSSProperties}
              onClick={() => unlocked && onOpenWorld(world)}
              aria-label={`${world.name}，${unlocked ? `${done}/${world.quests.length} 已完成` : "尚未解锁"}`}
            >
              <span className="node-number">{complete ? <Check /> : unlocked ? world.number : <LockKeyhole />}</span>
              <span className="node-copy">
                <strong>{world.name}</strong>
                <small>{unlocked ? `${done} / ${world.quests.length}` : "完成上一世界 60% 解锁"}</small>
              </span>
            </button>
          );
        })}
      </section>

      <aside className="mission-card">
        <div className="mission-time"><Sparkles aria-hidden="true" /> 今天 12 分钟</div>
        <p>当前任务</p>
        <h2>{nextQuest.title}</h2>
        <div className="mission-progress" aria-label={`当前世界进度 ${nextPercent}%`}>
          <span style={{ width: `${nextPercent}%` }} />
        </div>
        <button type="button" onClick={() => onOpenWorld(nextWorld)}>
          <Play aria-hidden="true" /> {completed.length === 0 ? "开始冒险" : "继续冒险"} <ChevronRight aria-hidden="true" />
        </button>
      </aside>

      <button className="coding-shortcut" type="button" onClick={onOpenCoding}>
        <TerminalSquare aria-hidden="true" />
        <span><small>特别训练</small><strong>14 关 MHA 手写工坊</strong></span>
        <ChevronRight aria-hidden="true" />
      </button>
    </main>
  );
}
